import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Color, Status, } from "@chessalpha/core";
import { ResizeObserver as Polyfill } from "@juggle/resize-observer";
import { Box, Container } from "@mui/material";
import { AdminButton } from "@ssf/auth-components";
import { useMutation } from "@tanstack/react-query";
import { Close, OpenInNew, Pencil } from "mdi-material-ui";
import { useEffect, useState } from "react";
import useResizeObserver from "use-resize-observer";
import { Game, GameAdmin, Jumps, OfferDrawButton, ResignButton, Result, StatusExplanation, serverTimeOffsetRef, trpcContext, undoInactiveTimeout, useActivityMonitor, useAttendances, useGame, useJumps, useKeyboardJumps, useValue, VideoHelp, } from "../../index.js";
import { StatusSnackbar } from "./index.js";
window.ResizeObserver = window.ResizeObserver || Polyfill;
export const InteractiveGame = ({ room, gid, game, nodes, archived = false, hideResignButton, minimumHalfmovesForResign = 0, disallowDrawOfferBeforeMove10, disallowDrawOfferBeforeMove30, minimumHalfmovesForDrawOffer, userId, resultProps = {}, children, MEMDB_URL, }) => {
    const [timestamp] = useState(Date.now());
    const [showAdmin, setShowAdmin] = useState(false);
    const { data: serverTimeOffset } = useValue(serverTimeOffsetRef)();
    const serverTimestamp = timestamp + (serverTimeOffset ?? 0);
    const { width = 300, height = 300, ref } = useResizeObserver();
    const reducedWidth = width - 8 * 2;
    const reducedHeight = height - (height / 12) * 2;
    const size = reducedWidth > reducedHeight ? reducedHeight : reducedWidth;
    const uid = userId ?? undefined;
    const { color, ...gameProps } = useGame({
        room,
        gid,
        game,
        nodes,
        uid,
        disabled: archived,
    });
    const jumpsProps = useJumps(gameProps.setNode, gameProps.node);
    useKeyboardJumps(gameProps.setNode, gameProps.node, (!showAdmin || archived) && game.status < Status.ABORTED);
    const trpc = trpcContext.useTRPC();
    const { mutate: undoMove } = useMutation(trpc.undoMove.mutationOptions());
    const { evaluateBrowserActivity } = useActivityMonitor(color, gameProps.position.toMove, gameProps.position.halfmove);
    const { mutate: submitEventLog } = useMutation(trpc.submitEventLog.mutationOptions());
    // biome-ignore lint/correctness/useExhaustiveDependencies: Unstable dependencies
    useEffect(() => {
        if (!archived && game.result !== 0) {
            const browserActivityAnalysis = evaluateBrowserActivity();
            // Only submit if player made at least 10 moves
            if (browserActivityAnalysis.totalMoves > 10) {
                submitEventLog({
                    room,
                    gid,
                    stats: {
                        ...browserActivityAnalysis,
                        color: color === Color.white
                            ? "white"
                            : color === Color.black
                                ? "black"
                                : undefined,
                        playerName: color === Color.white
                            ? game.white.name || game.white.uid
                            : color === Color.black
                                ? game.black.name || game.black.uid
                                : undefined,
                    },
                });
            }
        }
    }, [game.result, room, gid]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: Unstable dependencies
    useEffect(() => {
        const { node } = gameProps;
        if (!archived && node && game.status < Status.ABORTED) {
            const tryUndo = () => {
                const diff = Date.now() - node.timestamp;
                if (node.timestamp && diff > undoInactiveTimeout && node?.prev?.start) {
                    undoMove({ room, gid });
                }
                else if (node.prev?.start) {
                    return setTimeout(tryUndo, undoInactiveTimeout - diff);
                }
            };
            const timer = tryUndo();
            if (timer) {
                return () => clearTimeout(timer);
            }
        }
    }, [gameProps.node, game.status]);
    const { data: attendances } = useAttendances(`${room}-${gid}`, {
        disabled: archived,
    });
    return (_jsx(Container, { sx: [
            {
                display: "flex",
                justifyContent: "center",
                alignItems: "stretch",
                flex: "1 1 auto",
                "&": { maxWidth: "100%" },
            },
            showAdmin && !archived ? { minWidth: 900 } : {},
        ], children: _jsxs(Box, { ref: ref, sx: {
                display: "flex",
                justifyContent: "center",
                width: "100%",
                mr: 7,
            }, children: [_jsx(AdminButton, { buttonProps: {
                        component: "a",
                        href: `${MEMDB_URL}/EditTournamentRoundResultServlet?id=${game.meta?.pairingId}`,
                        sx: [
                            {
                                top: ({ spacing }) => spacing(16),
                                right: ({ spacing }) => spacing(-3),
                            },
                            !archived && game.meta?.memdb && game.meta.pairingId
                                ? {}
                                : { display: "none" },
                        ],
                        children: _jsx(OpenInNew, { fontSize: "small" }),
                    }, children: _jsx(AdminButton, { buttonProps: {
                            onClick: () => setShowAdmin(!showAdmin),
                            sx: {
                                top: ({ spacing }) => spacing(16),
                                right: ({ spacing }) => spacing(-3),
                                ...(archived && { display: "none" }),
                            },
                            children: showAdmin ? (_jsx(Close, { fontSize: "small" })) : (_jsx(Pencil, { fontSize: "small" })),
                        }, children: _jsxs(Game, { game: game, serverTimestamp: serverTimestamp, size: size, flipped: color === Color.black, color: archived || game.status >= Status.ABORTED ? 0 : 24, backgroundProps: {
                                children: (_jsxs(Box, { sx: {
                                        justifySelf: "flex-end",
                                        transform: "translateX(100%)",
                                        mr: -2,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                    }, children: [!archived && game.status === Status.STARTED && !!color && (_jsxs(_Fragment, { children: [(() => {
                                                    const requiredHalfmoves = minimumHalfmovesForDrawOffer ??
                                                        (disallowDrawOfferBeforeMove30
                                                            ? 60
                                                            : disallowDrawOfferBeforeMove10
                                                                ? 20
                                                                : 0);
                                                    // Special case: -1 means draw offers are completely disabled
                                                    if (requiredHalfmoves === -1) {
                                                        return false;
                                                    }
                                                    return (gameProps.position.halfmove >= requiredHalfmoves);
                                                })() && (_jsx(OfferDrawButton, { room: room, gid: gid, node: gameProps.node, userColor: color })), !hideResignButton &&
                                                    gameProps.position.halfmove >=
                                                        minimumHalfmovesForResign && (_jsx(ResignButton, { room: room, gid: gid }))] })), !showAdmin && game.status >= Status.ABORTED && (_jsx(Jumps, { ...jumpsProps }))] })),
                            }, attendances: attendances, ...gameProps, children: [_jsxs(Result, { node: gameProps.node, result: game.result, innerChildren: _jsx(StatusExplanation, { status: game.status }), closeable: true, ...resultProps, children: [resultProps.children, _jsx(VideoHelp, { status: game.status })] }), children] }) }) }), !archived && gameProps.node && (_jsx(StatusSnackbar, { node: gameProps.node, status: game.status })), !archived && showAdmin && gameProps.node && (_jsx(Box, { sx: {
                        ml: 4,
                        maxWidth: 360,
                        minWidth: 360,
                        height: "100%",
                    }, children: _jsx(GameAdmin, { room: room, gid: gid, game: game, node: gameProps.node, setNode: gameProps.setNode }) }))] }) }));
};
//# sourceMappingURL=InteractiveGame.js.map